import { useCallback } from 'react';

import { TimeQuestionType } from '@/playlist/games/timeGame/types';
import { PlayList, Question, SendAnswerRequest } from '@/playlist/types';

type SelectedTime = {
	hours: number;
	minutes: number;
};

interface UseTimeGameQuestionParams {
	playList: PlayList | null;
	currentQuestion?: Question;
	sendAnswerRequest: SendAnswerRequest;
	questionStartTs: number;
	onAnswerSubmitted: (isCorrect: boolean, isSecondAttempt: boolean) => void;
	freePlayCategoryId?: number;
}

interface UseTimeGameQuestionReturn {
	submitAnswer: (
		selectedTime: SelectedTime,
		isAnalogAnswer?: boolean,
		shouldSendRequest?: boolean,
		isSecondAttempt?: boolean,
		onRequestCompleted?: (response?: unknown) => void,
		onFinish?: () => void,
	) => void;
	checkAnswer: (
		selectedTime: SelectedTime,
		isAnalogAnswer?: boolean,
		isAnalogQuestion?: boolean,
	) => boolean;
}

export const parseTimeString = (value?: string): SelectedTime | null => {
	if (!value) return null;
	const match = value.match(/(\d{1,2})\s*[:.]\s*(\d{2})/);
	if (!match) return null;

	const hours = Number.parseInt(match[1], 10);
	const minutes = Number.parseInt(match[2], 10);
	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return null;
	}

	return { hours, minutes };
};

export const isSelectedTimeCorrect = (
	selected: SelectedTime,
	expected?: string,
	isAnalogAnswer?: boolean,
	isAnalogQuestion?: boolean,
): boolean => {
	const parsedExpected = parseTimeString(expected);
	if (!parsedExpected) return false;

	// Use modulo 12 comparison when either:
	// 1. User is answering with analog clock (can't specify AM/PM)
	// 2. Question shows analog clock (doesn't indicate AM/PM, so both 00:35 and 12:35 are correct)
	if (isAnalogAnswer || isAnalogQuestion) {
		return (
			parsedExpected.minutes === selected.minutes &&
			parsedExpected.hours % 12 === selected.hours % 12
		);
	}

	return parsedExpected.hours === selected.hours && parsedExpected.minutes === selected.minutes;
};

const formatSelectedTime = ({ hours, minutes }: SelectedTime): string => {
	const normalizedHours = ((hours % 24) + 24) % 24;
	return `${String(normalizedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Custom hook for handling time game question logic
 */
const useTimeGameQuestion = ({
	playList,
	currentQuestion,
	sendAnswerRequest,
	questionStartTs,
	onAnswerSubmitted,
	freePlayCategoryId,
}: UseTimeGameQuestionParams): UseTimeGameQuestionReturn => {
	// Check if answer is correct without submitting
	const checkAnswer = useCallback(
		(selectedTime: SelectedTime, isAnalogAnswer?: boolean, isAnalogQuestion?: boolean) => {
			if (!currentQuestion) {
				return false;
			}

			const answers = currentQuestion.answers ?? [];
			const correctAnswer = answers.find((answer) => answer?.is_correct) ?? answers[0];
			const parsedExpected = parseTimeString(correctAnswer?.answer_display);
			const selectedHours24 =
				isAnalogAnswer && parsedExpected
					? parsedExpected.hours // map analog selection to expected day part for submission
					: selectedTime.hours;
			const selectedForValidation: SelectedTime = {
				hours: selectedHours24,
				minutes: selectedTime.minutes,
			};

			return isSelectedTimeCorrect(
				selectedForValidation,
				correctAnswer?.answer_display,
				isAnalogAnswer,
				isAnalogQuestion,
			);
		},
		[currentQuestion],
	);

	const submitAnswer = useCallback(
		(
			selectedTime: SelectedTime,
			isAnalogAnswer?: boolean,
			shouldSendRequest = true,
			isSecondAttempt = false,
			onRequestCompleted?: (response?: unknown) => void,
			onFinish?: () => void,
		) => {
			if (!currentQuestion || !playList) {
				return;
			}

			const answers = currentQuestion.answers ?? [];
			const correctAnswer = answers.find((answer) => answer?.is_correct) ?? answers[0];
			const parsedExpected = parseTimeString(correctAnswer?.answer_display);
			const selectedHours24 =
				isAnalogAnswer && parsedExpected
					? parsedExpected.hours // map analog selection to expected day part for submission
					: selectedTime.hours;
			const selectedForValidation: SelectedTime = {
				hours: selectedHours24,
				minutes: selectedTime.minutes,
			};

			// Check if question shows analog clock (e.g., "AnalogToDigital")
			const questionType = currentQuestion.type || '';
			const isAnalogQuestion = questionType.includes(TimeQuestionType.AnalogToDigital);

			const isCorrect = isSelectedTimeCorrect(
				selectedForValidation,
				correctAnswer?.answer_display,
				isAnalogAnswer,
				isAnalogQuestion,
			);

			// Only send API request if shouldSendRequest is true
			if (shouldSendRequest) {
				const playlistId = playList.id;
				const questionId = currentQuestion.id;
				const elapsedSeconds = Math.max(
					0,
					Math.round((Date.now() - questionStartTs) / 1000),
				);

				if (playlistId !== null && playlistId !== undefined) {
					const requestBody = {
						playlist_section_answer: {
							user_learning_path_section_id: playlistId,
							question_id: questionId,
							answer_id: correctAnswer?.id,
							answer_texts: [formatSelectedTime(selectedForValidation)],
							answer_time_in_seconds: elapsedSeconds,
						},
					};

					sendAnswerRequest({
						playlistId,
						freePlayCategoryId: freePlayCategoryId ?? null,
						data: requestBody,
						lastQuestion: false,
						callback: onRequestCompleted,
						playtimeInSeconds: elapsedSeconds,
						isCorrect,
					});
				}
			} else if (onFinish) {
				// If not sending request but onFinish callback is provided,
				// call it separately (e.g., when battery depleted on first wrong attempt)
				onFinish();
			}

			// Notify parent component with both isCorrect and isSecondAttempt
			onAnswerSubmitted(isCorrect, isSecondAttempt);
		},
		[
			currentQuestion,
			playList,
			sendAnswerRequest,
			questionStartTs,
			onAnswerSubmitted,
			freePlayCategoryId,
		],
	);

	return {
		submitAnswer,
		checkAnswer,
	};
};

export default useTimeGameQuestion;
